import { ApiError, getHafasDepartures } from '../lib/api';
import type { DepartureGroup } from '../types/DepartureGroup';
import type { HafasDeparturesResponse } from '../types/HafasDeparturesResponse';
import { SupersedableStore } from './supersedable-store.svelte';

/** Message shown when the fetch fails with a non-{@link ApiError} exception. */
const GENERIC_ERROR = 'Failed to load departures.';
/** Message shown when the fetch exceeds {@link LOAD_TIMEOUT_MS}. */
const TIMEOUT_ERROR = 'Departures request timed out.';
/** How long to wait for the HAFAS response before giving up and unspinning the wheel. */
const LOAD_TIMEOUT_MS = 15_000;

/** Just the network+state machinery a caller can inject in tests. */
export interface DeparturesLoaderDependencies {
  fetcher: (extensionId: string, signal: AbortSignal) => Promise<HafasDeparturesResponse>;
  /** Injectable timeout for deterministic tests; production uses `LOAD_TIMEOUT_MS`. */
  timeoutMs?: number;
}

const DEFAULT_DEPENDENCIES: DeparturesLoaderDependencies = { fetcher: getHafasDepartures };

/**
 * Which HAFAS stop the board is currently showing. Autocomplete + favorite picks
 * fill real `lat`/`lon`; a URL restore that only carried `dep_ext_id` sets both
 * to `NaN`, so persistence callers must `Number.isFinite`-check before writing.
 */
export interface SelectedStop {
  extId: string;
  name: string;
  lat: number;
  lon: number;
}

/** Lifecycle of the last HAFAS-departures fetch. */
export type DeparturesState = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Reactive holder for the last `/v1/hafas-departures` response + the stop it
 * belongs to. Owns the fetch pipeline so both the tab component and the
 * app-level URL restore run through the same supersede + error path.
 * Instantiate a fresh copy per test to isolate state.
 */
export class DeparturesResponseStore extends SupersedableStore<HafasDeparturesResponse> {
  #dependencies: DeparturesLoaderDependencies;
  state = $state<DeparturesState>('idle');
  stop = $state<SelectedStop | undefined>(undefined);
  groups = $state<DepartureGroup[]>([]);
  /** UNIX-epoch seconds; the API sends `bigint` on the wire, we normalise to number. */
  fetchedAt = $state<number | undefined>(undefined);
  source = $state('');
  errorMessage = $state<string | undefined>(undefined);

  constructor(dependencies: DeparturesLoaderDependencies = DEFAULT_DEPENDENCIES) {
    super();
    this.#dependencies = dependencies;
  }

  /** Enters `loading` while a fetch is in flight. Keeps the previous rows on
   *  screen so the user isn't yanked back to a blank board mid-refresh. */
  setLoading(stop: SelectedStop): void {
    this.state = 'loading';
    this.stop = stop;
    this.errorMessage = undefined;
  }

  /**
   * Enters `ready` with a fresh board (`fetched_at_unix` narrowed from `bigint`
   * to `number`). Promotes `response.stop_name` onto the selected stop when it
   * supersedes the placeholder label from a URL restore.
   */
  setSuccess(response: HafasDeparturesResponse): void {
    this.state = 'ready';
    this.groups = response.groups;
    this.fetchedAt = Number(response.fetched_at_unix);
    this.source = response.source;
    this.errorMessage = undefined;
    if (this.stop && response.stop_name && response.stop_name !== this.stop.name) {
      this.stop = { ...this.stop, name: response.stop_name };
    }
  }

  /** Enters `error` with a user-visible message; keeps the previous board visible. */
  setError(message: string): void {
    this.state = 'error';
    this.errorMessage = message;
  }

  /**
   * Aborts any in-flight {@link load} and clears state back to `idle`. Used
   * when the user picks a new stop from scratch so stale rows don't flash.
   */
  reset(): void {
    this.abortInFlight();
    this.state = 'idle';
    this.stop = undefined;
    this.groups = [];
    this.fetchedAt = undefined;
    this.source = '';
    this.errorMessage = undefined;
  }

  /**
   * Loads the board for `stop`, superseding any in-flight request. Resolves
   * `true` on `ready`; `false` when aborted, superseded, or failed. Errors on
   * the still-current request set state to `error`; superseded errors leave
   * state to the newer caller or {@link reset}.
   */
  async load(stop: SelectedStop): Promise<boolean> {
    this.setLoading(stop);
    return this.runWithSupersede(
      (signal) => this.#dependencies.fetcher(stop.extId, signal),
      this.#dependencies.timeoutMs ?? LOAD_TIMEOUT_MS,
    );
  }

  protected override applyResponse(response: HafasDeparturesResponse): void {
    this.setSuccess(response);
  }

  protected override classifyError(error: unknown): string {
    if (error instanceof ApiError) return `Departures unavailable (HTTP ${String(error.status)})`;
    return GENERIC_ERROR;
  }

  protected override timeoutMessage(): string {
    return TIMEOUT_ERROR;
  }
}
